{
  "name": "vermilion",
  "displayName": "Vermilion Editorial",
  "description": "Poster-red editorial system — vermilion display headings, ink text, warm creams and hairline rules",
  "whenToUse": "Editorial and annual-report work that wants a strong red display voice: covers, feature-style sections, reports built around a few large headings.",
  "version": "1.0.0",
  "colors": {
    "primary": "#282829",
    "secondary": "#58595B",
    "accent": "#EF4130",
    "text": "#282829",
    "background": "#FFFFFF",
    "border": "#E8E2DC",
    "textPrimary": "#282829",
    "textSecondary": "#58595B",
    "textMuted": "#939598",
    "borderPrimary": "#C7C8CA",
    "borderSecondary": "#EFEAE4",
    "backgroundPrimary": "#FFFFFF",
    "backgroundSecondary": "#FAF7F5",
    "accent4": "#F4857A",
    "accent5": "#F9C4BE",
    "accent6": "#939598"
  },
  "palette": {
    "rule": "#C7C8CA",
    "textMuted": "#939598",
    "onPrimary": "#FFFFFF",
    "surfaceInverse": "#282829",
    "positive": "#2E7D4F",
    "negative": "#B42318",
    "chart": ["accent", "primary", "textMuted", "accent4", "rule", "accent5"]
  },
  "fonts": {
    "heading": {
      "family": "Arial",
      "size": 24
    },
    "body": {
      "family": "Calibri",
      "size": 10.5
    },
    "mono": {
      "family": "Courier New",
      "size": 10
    },
    "light": {
      "family": "Arial",
      "size": 24
    }
  },
  "page": {
    "size": "A4",
    "margins": {
      "top": 1152,
      "bottom": 1152,
      "left": 1152,
      "right": 1152,
      "header": 620,
      "footer": 620,
      "gutter": 0
    }
  },
  "typography": {
    "roles": {
      "eyebrow": {
        "face": "heading",
        "size": 8,
        "weight": 700,
        "case": "upper",
        "tracking": 8,
        "color": "accent",
        "spaceAfter": 4
      },
      "display": {
        "face": "heading",
        "weight": 700,
        "color": "accent",
        "lineHeight": 1.05,
        "spaceAfter": 8
      },
      "stat": {
        "face": "heading",
        "weight": 700,
        "color": "accent",
        "lineHeight": 1
      },
      "quote": {
        "face": "body",
        "color": "textSecondary",
        "lineHeight": 1.3
      },
      "label": {
        "face": "body",
        "size": 9,
        "weight": 700,
        "color": "textSecondary"
      },
      "footer": {
        "face": "body",
        "size": 8,
        "color": "textMuted"
      },
      "tableHeader": {
        "face": "body",
        "size": 9.5,
        "weight": 700,
        "color": "accent"
      },
      "tableCell": {
        "face": "body",
        "size": 9.5,
        "color": "textPrimary"
      },
      "chartLabel": {
        "face": "body",
        "size": 9,
        "weight": 400,
        "color": "textSecondary"
      },
      "tracker": {
        "face": "body",
        "size": 8,
        "case": "upper",
        "tracking": 6,
        "color": "textMuted"
      },
      "source": {
        "face": "body",
        "size": 8,
        "color": "textMuted"
      }
    },
    "scale": {
      "a4": {
        "base": 10.5,
        "ratio": 1.25,
        "baselinePt": 2
      },
      "letter": {
        "base": 10.5,
        "ratio": 1.25,
        "baselinePt": 2
      }
    }
  },
  "spacing": {
    "basePt": 8,
    "blockGap": {
      "tight": 4,
      "normal": 8,
      "loose": 16
    },
    "canvas": {
      "a4": {
        "safeAreaIn": 0.8,
        "gutterIn": 0,
        "columns": 12
      },
      "letter": {
        "safeAreaIn": 0.8,
        "gutterIn": 0,
        "columns": 12
      }
    }
  },
  "chrome": {
    "runningHead": {
      "type": "tracker",
      "color": "textMuted",
      "rule": {
        "weightPt": 0.5,
        "color": "rule"
      },
      "alignment": "right"
    },
    "tracker": {
      "type": "tracker",
      "color": "textMuted",
      "alignment": "right"
    },
    "actionTitle": {
      "type": "display",
      "color": "accent"
    },
    "keyTakeaways": {
      "type": "label",
      "color": "primary",
      "rule": {
        "weightPt": 1.5,
        "color": "accent"
      },
      "padPt": 8
    },
    "sourceLine": {
      "type": "source",
      "color": "textMuted"
    },
    "confidentialFooter": {
      "type": "footer",
      "color": "textMuted",
      "rule": {
        "weightPt": 0.5,
        "color": "rule"
      },
      "alignment": "left"
    },
    "logoSlot": {
      "alignment": "right"
    },
    "cover": {
      "type": "display",
      "color": "accent",
      "rule": {
        "weightPt": 4,
        "color": "accent"
      }
    }
  },
  "motif": {
    "kind": "rule",
    "color": "accent",
    "weightPt": 4,
    "placement": "top"
  },
  "styles": {
    "normal": {
      "font": "body",
      "color": "textPrimary",
      "lineSpacing": {
        "type": "multiple",
        "value": 1.3
      },
      "alignment": "left",
      "spacing": {
        "after": 8
      }
    },
    "heading1": {
      "font": "heading",
      "size": 25,
      "bold": false,
      "color": "accent",
      "characterSpacing": {
        "type": "expanded",
        "value": 50
      },
      "spacing": {
        "before": 22,
        "after": 12
      },
      "keepNext": true,
      "alignment": "left"
    },
    "heading2": {
      "font": "heading",
      "size": 12.5,
      "bold": true,
      "color": "accent",
      "characterSpacing": {
        "type": "expanded",
        "value": 30
      },
      "spacing": {
        "before": 16,
        "after": 7
      },
      "keepNext": true,
      "alignment": "left"
    },
    "heading3": {
      "font": "body",
      "size": 11,
      "bold": true,
      "color": "textPrimary",
      "spacing": {
        "before": 12,
        "after": 5
      },
      "keepNext": true,
      "alignment": "left"
    },
    "heading4": {
      "font": "body",
      "size": 10.5,
      "bold": true,
      "color": "textSecondary",
      "spacing": {
        "before": 10,
        "after": 4
      },
      "keepNext": true,
      "alignment": "left"
    },
    "heading5": {
      "font": "body",
      "size": 10.5,
      "bold": false,
      "italic": true,
      "color": "textSecondary",
      "spacing": {
        "before": 9,
        "after": 4
      },
      "keepNext": true,
      "alignment": "left"
    },
    "heading6": {
      "font": "body",
      "size": 10,
      "bold": true,
      "color": "textMuted",
      "characterSpacing": {
        "type": "expanded",
        "value": 30
      },
      "spacing": {
        "before": 9,
        "after": 4
      },
      "keepNext": true,
      "alignment": "left"
    },
    "title": {
      "font": "heading",
      "size": 38,
      "bold": true,
      "color": "primary",
      "alignment": "left",
      "spacing": {
        "before": 0,
        "after": 14
      }
    },
    "subtitle": {
      "font": "body",
      "size": 14,
      "color": "textSecondary",
      "alignment": "left",
      "spacing": {
        "before": 0,
        "after": 10
      }
    }
  },
  "componentDefaults": {
    "table": {
      "width": 100,
      "borderColor": "#C7C8CA",
      "borderSize": 0.5,
      "hideBorders": {
        "top": true,
        "left": true,
        "right": true,
        "insideVertical": true
      },
      "cellDefaults": {
        "font": {
          "size": 9.5
        },
        "padding": {
          "top": 7,
          "bottom": 7
        },
        "verticalAlignment": "middle"
      },
      "headerCellDefaults": {
        "color": "#EF4130",
        "font": {
          "size": 9.5,
          "bold": true
        },
        "padding": {
          "top": 0,
          "bottom": 8
        },
        "verticalAlignment": "bottom",
        "borderColor": {
          "bottom": "#EF4130"
        },
        "borderSize": {
          "bottom": 0.5
        }
      }
    },
    "list": {
      "format": "bullet",
      "bullet": "—",
      "spacing": {
        "item": 2
      }
    },
    "image": {
      "alignment": "center"
    },
    "statistic": {
      "alignment": "center"
    },
    "section": {
      "pageBreak": false
    }
  }
}
